import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getActiveRef } from "@/lib/affiliateTracking";

// Scoped SDK (own namespace) so it can never collide with other PayPal buttons.
function loadPayPalSubsSdk(clientId) {
  if (window.paypalSubs) return Promise.resolve(window.paypalSubs);
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&vault=true&intent=subscription&currency=USD&components=buttons`;
    s.setAttribute("data-namespace", "paypalSubs");
    s.async = true;
    s.onload = () => resolve(window.paypalSubs);
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

const detailMsg = (e, f) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : d?.message || f; };

export function PayPalEnroll({ planKey, planLabel }) {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [consent, setConsent] = useState(false);
  const [st, setSt] = useState({ kind: "idle", msg: "" });
  const box = useRef(null);
  const consentRef = useRef(false);
  consentRef.current = consent;
  useEffect(() => { api.get("/subscriptions/plans").then(({ data }) => setInfo(data)).catch(() => setInfo(null)); }, []);
  const plan = info?.plans?.find((p) => p.key === planKey);
  const total = ((plan?.total_cents || 0) / 100).toFixed(2);

  useEffect(() => {
    if (!user || !info?.paypal?.client_id || !box.current) return undefined;
    let buttons;
    let intent = null;
    loadPayPalSubsSdk(info.paypal.client_id).then((paypal) => {
      if (!box.current) return;
      box.current.innerHTML = "";
      buttons = paypal.Buttons({
        style: { shape: "pill", color: "gold", label: "subscribe" },
        onClick: (_d, actions) => {
          if (!consentRef.current) { setSt({ kind: "err", msg: "Please agree to the recurring billing terms first." }); return actions.reject(); }
          return actions.resolve();
        },
        createSubscription: async (_d, actions) => {
          const { data } = await api.post("/paypal/subscriptions/start", { plan_key: planKey, consent: true, ref_code: getActiveRef() || null });
          intent = data;
          return actions.subscription.create({ plan_id: data.plan_id, custom_id: data.custom_id });
        },
        onApprove: async (data) => {
          setSt({ kind: "loading", msg: "Confirming with PayPal…" });
          try {
            const { data: r } = await api.post("/paypal/subscriptions/confirm", { intent_id: intent.intent_id, subscription_id: data.subscriptionID });
            setSt({ kind: "ok", msg: r.subscription.entitled ? "Subscribed — payment confirmed. Your box is active." : r.note });
          } catch (e) { setSt({ kind: "err", msg: detailMsg(e, "We couldn't confirm your subscription. Contact support before retrying.") }); }
        },
        onError: (e) => setSt({ kind: "err", msg: detailMsg(e, "PayPal could not start the subscription. You were not charged.") }),
      });
      buttons.render(box.current);
    }).catch(() => setSt({ kind: "err", msg: "PayPal could not load. Please refresh." }));
    return () => { try { buttons?.close(); } catch { /* noop */ } };
  }, [user, info, planKey]);

  if (!user) {
    return (
      <div className="text-center" data-testid="enroll-login-required">
        <h2 className="font-anton uppercase text-3xl text-white">Subscribe</h2>
        <p className="text-white/60 text-sm mt-2">Log in so you can manage or cancel your box from your account.</p>
        <Link to="/auth/login?next=/lifted-monthly-box" className="btn-lifted mt-5 inline-flex" data-testid="enroll-login-btn">Log in to subscribe</Link>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="paypal-enroll">
      <h2 className="font-anton uppercase text-3xl text-white text-center">Subscribe with PayPal</h2>
      <div className="text-center text-sm text-white/70" data-testid="enroll-plan-summary">
        {planLabel} — <span className="text-lifted-green">${total}/month</span>
      </div>
      <label className="flex gap-2 text-xs text-white/75">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="enroll-consent" />
        I authorize PayPal to charge ${total} USD every month for the {planLabel} until I cancel, per the terms above. Cancel anytime from My Monthly Box; paid months stay active.
      </label>
      <div ref={box} data-testid="paypal-subscribe-buttons" className="min-h-[50px]" />
      {st.kind === "ok" && <div className="text-sm text-lifted-green flex gap-2" data-testid="enroll-success"><ShieldCheck className="h-4 w-4" />{st.msg} <Link to="/account/monthly-box" className="underline">Manage</Link></div>}
      {st.kind === "loading" && <div className="text-sm text-white/60">{st.msg}</div>}
      {st.kind === "err" && <div className="text-sm text-rose-300" data-testid="enroll-error">{st.msg}</div>}
    </div>
  );
}
